import { i18nService } from '../../i18n/I18nService';
import { getHashParams } from '../../dsl/hashParams';
import { authService } from '../../auth/AuthService';
import { fetchPlaces } from '../../api/placesApi';
import { fetchAvailability, fetchSubscriptionBalance, createBooking, ApiError } from '../../api/bookingApi';
import { buildMonthDays } from '../calendarGrid';
import { formatMinutesAsTime as formatTime, formatEuros, formatCentsAsEuros, dateKeyLocal as dateKey, isSameLocalDay as isSameDay } from '../../utils/format';
// Fallback used only before the first availability response arrives — once
// loaded, the server's own stepMinutes (server/availability.mjs) takes over,
// so a server-side change to the booking granularity doesn't silently
// desync from this hardcoded value.
const DEFAULT_STEP_MINUTES = 15;
const MIN_BOOKING_MINUTES = 60;
// Same 24h lead-time rule as cancellation (server/routes/booking.mjs's
// MIN_BOOKING_LEAD_SECONDS) — a slot can't be booked less than this far
// ahead of now.
const MIN_LEAD_SECONDS = 24 * 60 * 60;
function t(key, params) {
    return i18nService.t(key, params);
}
function dateShortLabel(date) {
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    return `${day}.${month}`;
}
// Mirrors the server's parseDateParam: `dateStr` is a UTC calendar day, so a
// slot's instant is that UTC midnight plus its minutes-of-day offset.
function slotStartUnix(dateStr, startMinutes) {
    const [year, month, day] = dateStr.split('-').map(Number);
    return Date.UTC(year, month - 1, day) / 1000 + startMinutes * 60;
}
// crypto.randomUUID() is exposed only in a secure context, so it is missing on
// a plain-HTTP origin that isn't localhost (a LAN IP during phone testing, an
// HTTP deployment — see COOKIE_SECURE in .env.example). Letting it throw would
// leave the submit button stuck in its disabled "submitting" state, since the
// throw happens before the request whose .catch restores it.
// crypto.getRandomValues, unlike randomUUID, is available either way.
function createIdempotencyKey() {
    if (typeof crypto.randomUUID === 'function')
        return crypto.randomUUID();
    const bytes = new Uint8Array(16);
    crypto.getRandomValues(bytes);
    return Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
}
function buildErrorContent(message) {
    const wrapper = document.createElement('div');
    const status = document.createElement('p');
    status.className = 'booking__status';
    status.textContent = message;
    const link = document.createElement('a');
    link.href = '#page=home';
    link.className = 'booking__back-link';
    link.textContent = t('booking.backHomeLabel');
    wrapper.append(status, link);
    return wrapper;
}
export function renderBooking(_block) {
    const rawPlaceId = getHashParams().get('place');
    if (!authService.isAuthorized) {
        window.location.hash = rawPlaceId ? `#page=auth&place=${rawPlaceId}` : '#page=auth';
        return document.createElement('section');
    }
    const section = document.createElement('section');
    section.className = 'booking';
    const placeId = Number(rawPlaceId);
    if (rawPlaceId === null || !Number.isInteger(placeId)) {
        section.appendChild(buildErrorContent(t('booking.notFound')));
        return section;
    }
    const status = document.createElement('p');
    status.className = 'booking__status';
    status.textContent = t('booking.loading');
    section.appendChild(status);
    fetchPlaces('all')
        .then((places) => {
        const place = places.find((candidate) => candidate.id === placeId);
        section.innerHTML = '';
        if (!place) {
            section.appendChild(buildErrorContent(t('booking.notFound')));
            return;
        }
        section.appendChild(buildBookingUI(place));
    })
        .catch((error) => {
        console.error('Failed to load place:', error);
        section.innerHTML = '';
        section.appendChild(buildErrorContent(t('booking.error')));
    });
    return section;
}
function buildBookingUI(place) {
    const locale = i18nService.getLocale();
    const root = document.createElement('div');
    root.className = 'booking__content';
    // ---- Gallery ----
    const gallery = document.createElement('div');
    gallery.className = 'booking__gallery';
    const photo = document.createElement('img');
    photo.className = 'booking__gallery-photo';
    photo.src = place.photo ?? '';
    photo.alt = place.name[locale];
    gallery.appendChild(photo);
    root.appendChild(gallery);
    // ---- Title + meta ----
    const info = document.createElement('div');
    info.className = 'booking__info';
    const title = document.createElement('h1');
    title.className = 'section-title booking__title';
    title.textContent = place.name[locale];
    const meta = document.createElement('p');
    meta.className = 'booking__meta';
    meta.textContent = place.capacity === 1 ? t('booking.capacityOne') : t('booking.capacity', { count: place.capacity });
    info.append(title, meta);
    root.appendChild(info);
    root.appendChild(document.createElement('hr')).className = 'booking__divider';
    // ---- Calendar ----
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    let viewYear = today.getFullYear();
    let viewMonth = today.getMonth();
    let selectedDate = new Date(today);
    const calendar = document.createElement('div');
    calendar.className = 'booking__cal';
    const calHead = document.createElement('div');
    calHead.className = 'booking__cal-head';
    const prevButton = document.createElement('button');
    prevButton.type = 'button';
    prevButton.className = 'booking__cal-nav';
    prevButton.setAttribute('aria-label', 'Previous month');
    prevButton.innerHTML =
        '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M15 18l-6-6 6-6" stroke-linecap="round" stroke-linejoin="round"/></svg>';
    const nextButton = document.createElement('button');
    nextButton.type = 'button';
    nextButton.className = 'booking__cal-nav';
    nextButton.setAttribute('aria-label', 'Next month');
    nextButton.innerHTML =
        '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M9 6l6 6-6 6" stroke-linecap="round" stroke-linejoin="round"/></svg>';
    const monthLabel = document.createElement('span');
    monthLabel.className = 'booking__cal-month';
    calHead.append(prevButton, monthLabel, nextButton);
    const weekdaysRow = document.createElement('div');
    weekdaysRow.className = 'booking__cal-weekdays';
    for (let day = 0; day < 7; day += 1) {
        const cell = document.createElement('span');
        cell.className = 'booking__cal-weekday';
        cell.textContent = t(`booking.calendar.weekdays.${day}`);
        weekdaysRow.appendChild(cell);
    }
    const calGrid = document.createElement('div');
    calGrid.className = 'booking__cal-grid';
    calendar.append(calHead, weekdaysRow, calGrid);
    root.appendChild(calendar);
    root.appendChild(document.createElement('hr')).className = 'booking__divider';
    // ---- Availability + summary + sticky bar ----
    const availability = document.createElement('div');
    availability.className = 'booking__availability';
    root.appendChild(availability);
    const barSpace = document.createElement('div');
    barSpace.className = 'booking__bar-space';
    root.appendChild(barSpace);
    const bar = document.createElement('div');
    bar.className = 'booking__bar';
    const totalBlock = document.createElement('div');
    const totalLabel = document.createElement('p');
    totalLabel.className = 'booking__bar-label';
    totalLabel.textContent = t('booking.total');
    const totalPrice = document.createElement('p');
    totalPrice.className = 'booking__bar-price';
    totalPrice.textContent = '—';
    totalBlock.append(totalLabel, totalPrice);
    const submitButton = document.createElement('button');
    submitButton.type = 'button';
    submitButton.className = 'cta auth__submit booking__submit';
    submitButton.textContent = t('booking.submit');
    submitButton.disabled = true;
    bar.append(totalBlock, submitButton);
    root.appendChild(bar);
    const balanceLine = document.createElement('p');
    balanceLine.className = 'booking__balance-line';
    root.appendChild(balanceLine);
    const homeLink = document.createElement('a');
    homeLink.href = '#page=home';
    homeLink.className = 'booking__home-link';
    homeLink.textContent = t('booking.backHomeLabel');
    root.appendChild(homeLink);
    let selectionState = {
        activeWindow: 0,
        start: null,
        end: null,
    };
    let currentWindows = [];
    let currentStepMinutes = DEFAULT_STEP_MINUTES;
    let bookingConfirmed = false;
    let subscriptionLeftMinutes = undefined;
    let forceHourlyMode = false;
    // Reused across a retried submit (button re-enabled after a failed
    // request) for the same slot selection, so the server can dedupe a
    // double-submit instead of creating two bookings. Cleared whenever the
    // selected slot changes or a booking succeeds, since that's a new intent.
    let pendingIdempotencyKey = null;
    function renderBalanceLine() {
        if (subscriptionLeftMinutes === undefined) {
            balanceLine.textContent = '';
            return;
        }
        if (subscriptionLeftMinutes !== null && subscriptionLeftMinutes > 0) {
            balanceLine.textContent = t('booking.subscriptionTimeLeft', { time: formatTime(subscriptionLeftMinutes) });
        }
        else if (place.pricePerHour != null) {
            balanceLine.textContent = t('booking.hourlyRateLabel', { price: formatEuros(place.pricePerHour) });
        }
        else {
            balanceLine.textContent = '';
        }
    }
    function hasMinLeadTime(startMinutes) {
        return slotStartUnix(dateKey(selectedDate), startMinutes) - Date.now() / 1000 >= MIN_LEAD_SECONDS;
    }
    function renderSummaryAndBar() {
        const errorEl = availability.querySelector('.booking__error');
        if (errorEl)
            errorEl.remove();
        if (bookingConfirmed)
            return;
        const summary = availability.querySelector('.booking__summary');
        if (!summary)
            return;
        const warningEl = availability.querySelector('.booking__insufficient-warning');
        if (warningEl)
            warningEl.remove();
        const isUsingSubscription = subscriptionLeftMinutes !== undefined &&
            subscriptionLeftMinutes !== null &&
            subscriptionLeftMinutes > 0 &&
            !forceHourlyMode;
        totalBlock.style.display = isUsingSubscription ? 'none' : 'block';
        if (selectionState.start !== null && selectionState.end !== null) {
            const duration = selectionState.end - selectionState.start;
            summary.className = 'booking__summary';
            summary.textContent = t('booking.summary.result', {
                start: formatTime(selectionState.start),
                end: formatTime(selectionState.end),
                duration,
            });
            totalPrice.textContent =
                place.pricePerHour == null ? '—' : formatEuros(Math.round((duration / 60) * place.pricePerHour * 100) / 100);
            submitButton.disabled = !hasMinLeadTime(selectionState.start);
            if (subscriptionLeftMinutes !== undefined &&
                subscriptionLeftMinutes !== null &&
                subscriptionLeftMinutes > 0 &&
                subscriptionLeftMinutes < duration &&
                !forceHourlyMode) {
                const warning = document.createElement('p');
                warning.className = 'auth__error booking__insufficient-warning';
                warning.textContent = t('booking.insufficientWarning', {
                    available: formatTime(subscriptionLeftMinutes),
                    selected: formatTime(duration),
                });
                summary.insertAdjacentElement('afterend', warning);
            }
        }
        else {
            summary.className = 'booking__summary booking__summary--placeholder';
            summary.textContent = t('booking.summary.placeholder');
            totalPrice.textContent = '—';
            submitButton.disabled = true;
        }
    }
    function renderAvailabilityBody() {
        availability.innerHTML = '';
        const statusRow = document.createElement('div');
        statusRow.className = 'booking__availability-row';
        const statusText = document.createElement('span');
        statusText.className = 'booking__availability-status';
        const stepText = document.createElement('span');
        stepText.className = 'booking__availability-step';
        stepText.textContent = t('booking.availability.step', { step: currentStepMinutes });
        statusRow.append(statusText, stepText);
        availability.appendChild(statusRow);
        if (currentWindows.length === 0) {
            const emptyMessage = document.createElement('p');
            emptyMessage.className = 'booking__empty';
            emptyMessage.textContent = t('booking.availability.fullyBooked');
            availability.appendChild(emptyMessage);
            statusText.textContent = t('booking.availability.free', { date: dateShortLabel(selectedDate) });
            const summary = document.createElement('p');
            summary.className = 'booking__summary';
            availability.appendChild(summary);
            totalPrice.textContent = '—';
            submitButton.disabled = true;
            return;
        }
        statusText.textContent = t('booking.availability.free', { date: dateShortLabel(selectedDate) });
        const windowGroup = document.createElement('div');
        windowGroup.className = 'booking__chip-group';
        currentWindows.forEach((window, index) => {
            const chip = document.createElement('button');
            chip.type = 'button';
            chip.className = 'booking__chip' + (index === selectionState.activeWindow ? ' booking__chip--selected' : '');
            chip.textContent = `${formatTime(window[0])}–${formatTime(window[1])}`;
            chip.addEventListener('click', () => {
                selectionState = { activeWindow: index, start: null, end: null };
                pendingIdempotencyKey = null;
                renderAvailabilityBody();
            });
            windowGroup.appendChild(chip);
        });
        availability.appendChild(windowGroup);
        const activeWindow = currentWindows[selectionState.activeWindow];
        if (selectionState.start === null || selectionState.start < activeWindow[0] || selectionState.start > activeWindow[1] - MIN_BOOKING_MINUTES) {
            selectionState.start = activeWindow[0];
        }
        if (selectionState.end === null || selectionState.end < selectionState.start + MIN_BOOKING_MINUTES || selectionState.end > activeWindow[1]) {
            selectionState.end = null;
        }
        const timeRow = document.createElement('div');
        timeRow.className = 'booking__time-row';
        const startField = document.createElement('div');
        startField.className = 'booking__time-field';
        const startLabel = document.createElement('label');
        startLabel.className = 'booking__chip-label';
        startLabel.textContent = t('booking.availability.start');
        const startSelect = document.createElement('select');
        startSelect.className = 'booking__time-select';
        for (let time = activeWindow[0]; time <= activeWindow[1] - MIN_BOOKING_MINUTES; time += currentStepMinutes) {
            const option = document.createElement('option');
            option.value = String(time);
            option.textContent = formatTime(time);
            if (time === selectionState.start)
                option.selected = true;
            startSelect.appendChild(option);
        }
        startSelect.addEventListener('change', () => {
            selectionState.start = Number(startSelect.value);
            selectionState.end = null;
            pendingIdempotencyKey = null;
            renderAvailabilityBody();
        });
        startField.append(startLabel, startSelect);
        const sep = document.createElement('span');
        sep.className = 'booking__time-sep';
        sep.textContent = '—';
        const endField = document.createElement('div');
        endField.className = 'booking__time-field';
        const endLabel = document.createElement('label');
        endLabel.className = 'booking__chip-label';
        endLabel.textContent = t('booking.availability.end');
        const endSelect = document.createElement('select');
        endSelect.className = 'booking__time-select';
        const placeholderOption = document.createElement('option');
        placeholderOption.textContent = '—';
        placeholderOption.disabled = true;
        placeholderOption.selected = selectionState.end === null;
        endSelect.appendChild(placeholderOption);
        for (let time = selectionState.start + MIN_BOOKING_MINUTES; time <= activeWindow[1]; time += currentStepMinutes) {
            const option = document.createElement('option');
            option.value = String(time);
            option.textContent = formatTime(time);
            if (time === selectionState.end)
                option.selected = true;
            endSelect.appendChild(option);
        }
        endSelect.addEventListener('change', () => {
            selectionState.end = Number(endSelect.value);
            pendingIdempotencyKey = null;
            renderSummaryAndBar();
        });
        endField.append(endLabel, endSelect);
        timeRow.append(startField, sep, endField);
        availability.appendChild(timeRow);
        const summary = document.createElement('p');
        summary.className = 'booking__summary';
        availability.appendChild(summary);
        renderSummaryAndBar();
    }
    function showAvailabilityError(message) {
        availability.innerHTML = '';
        const errorMessage = document.createElement('p');
        errorMessage.className = 'booking__empty';
        errorMessage.textContent = message;
        availability.appendChild(errorMessage);
        totalPrice.textContent = '—';
        submitButton.disabled = true;
    }
    function loadAvailability() {
        bookingConfirmed = false;
        // A different day is a new intent, exactly like switching window/start/end
        // below — reusing the key would make the server replay the booking made on
        // the previous day instead of booking this one.
        pendingIdempotencyKey = null;
        submitButton.textContent = t('booking.submit');
        availability.innerHTML = `<p class="booking__status">${t('booking.loading')}</p>`;
        fetchAvailability(place.id, dateKey(selectedDate))
            .then((response) => {
            selectionState = { activeWindow: 0, start: null, end: null };
            currentStepMinutes = response.stepMinutes;
            if (response.closed) {
                showAvailabilityError(t('booking.availability.closed'));
                return;
            }
            currentWindows = response.windows.filter((window) => window[1] - window[0] >= MIN_BOOKING_MINUTES);
            renderAvailabilityBody();
        })
            .catch((error) => {
            console.error('Failed to load availability:', error);
            showAvailabilityError(t('booking.error'));
        });
    }
    function renderCalendarGrid() {
        monthLabel.textContent = `${t(`booking.calendar.months.${viewMonth}`)} ${viewYear}`;
        calGrid.innerHTML = '';
        for (const date of buildMonthDays(viewYear, viewMonth)) {
            if (!date) {
                const empty = document.createElement('span');
                empty.className = 'booking__cal-day booking__cal-day--empty';
                calGrid.appendChild(empty);
                continue;
            }
            const isPast = date < today;
            const button = document.createElement('button');
            button.type = 'button';
            button.className = 'booking__cal-day';
            button.textContent = String(date.getDate());
            if (date.getDay() === 0 || date.getDay() === 6)
                button.classList.add('booking__cal-day--muted');
            if (isSameDay(date, today))
                button.classList.add('booking__cal-day--today');
            if (isSameDay(date, selectedDate))
                button.classList.add('booking__cal-day--selected');
            if (isPast) {
                button.classList.add('booking__cal-day--disabled');
                button.disabled = true;
            }
            else {
                button.addEventListener('click', () => {
                    selectedDate = date;
                    renderCalendarGrid();
                    loadAvailability();
                });
            }
            calGrid.appendChild(button);
        }
    }
    function openInsufficientTimeModal() {
        const overlay = document.createElement('div');
        overlay.className = 'booking__modal-overlay';
        overlay.addEventListener('click', (event) => {
            if (event.target === overlay)
                overlay.remove();
        });
        const modal = document.createElement('div');
        modal.className = 'booking__modal';
        const body = document.createElement('p');
        body.className = 'booking__modal-text';
        body.textContent = t('booking.insufficientTimeModal.body');
        const contactsLabel = document.createElement('p');
        contactsLabel.className = 'booking__modal-contacts-label';
        contactsLabel.textContent = t('booking.insufficientTimeModal.contactsLabel');
        const contacts = document.createElement('div');
        contacts.className = 'booking__modal-contacts';
        const phone1Link = document.createElement('a');
        phone1Link.href = `tel:+35799213755`;
        phone1Link.textContent = `${t('booking.insufficientTimeModal.phone1Name')}: ${t('booking.insufficientTimeModal.phone1')}`;
        const phone1Line = document.createElement('p');
        phone1Line.className = 'booking__modal-contact-line';
        phone1Line.appendChild(phone1Link);
        const phone2Link = document.createElement('a');
        phone2Link.href = `tel:+35796793008`;
        phone2Link.textContent = `${t('booking.insufficientTimeModal.phone2Name')}: ${t('booking.insufficientTimeModal.phone2')}`;
        const phone2Line = document.createElement('p');
        phone2Line.className = 'booking__modal-contact-line';
        phone2Line.appendChild(phone2Link);
        const emailLink = document.createElement('a');
        emailLink.href = `mailto:ladyspace.cyprus@gmail.com`;
        emailLink.textContent = t('booking.insufficientTimeModal.email');
        const emailLine = document.createElement('p');
        emailLine.className = 'booking__modal-contact-line';
        emailLine.appendChild(emailLink);
        contacts.append(phone1Line, phone2Line, emailLine);
        const actions = document.createElement('div');
        actions.className = 'booking__modal-actions';
        const closeButton = document.createElement('button');
        closeButton.type = 'button';
        closeButton.className = 'cta';
        closeButton.textContent = t('booking.insufficientTimeModal.closeButton');
        closeButton.addEventListener('click', () => overlay.remove());
        const hourlyButton = document.createElement('button');
        hourlyButton.type = 'button';
        hourlyButton.className = 'cta';
        hourlyButton.textContent = t('booking.insufficientTimeModal.hourlyButton');
        hourlyButton.addEventListener('click', () => {
            forceHourlyMode = true;
            overlay.remove();
            renderSummaryAndBar();
        });
        actions.append(hourlyButton, closeButton);
        modal.append(body, contactsLabel, contacts, actions);
        overlay.appendChild(modal);
        document.body.appendChild(overlay);
    }
    function openLeadTimeBlockedModal() {
        const overlay = document.createElement('div');
        overlay.className = 'booking__modal-overlay';
        overlay.addEventListener('click', (event) => {
            if (event.target === overlay)
                overlay.remove();
        });
        const modal = document.createElement('div');
        modal.className = 'booking__modal';
        const body = document.createElement('p');
        body.className = 'booking__modal-text';
        body.textContent = t('booking.leadTimeModal.body');
        const contactsLabel = document.createElement('p');
        contactsLabel.className = 'booking__modal-contacts-label';
        contactsLabel.textContent = t('booking.insufficientTimeModal.contactsLabel');
        const contacts = document.createElement('div');
        contacts.className = 'booking__modal-contacts';
        const phone1Link = document.createElement('a');
        phone1Link.href = `tel:+35799213755`;
        phone1Link.textContent = `${t('booking.insufficientTimeModal.phone1Name')}: ${t('booking.insufficientTimeModal.phone1')}`;
        const phone1Line = document.createElement('p');
        phone1Line.className = 'booking__modal-contact-line';
        phone1Line.appendChild(phone1Link);
        const phone2Link = document.createElement('a');
        phone2Link.href = `tel:+35796793008`;
        phone2Link.textContent = `${t('booking.insufficientTimeModal.phone2Name')}: ${t('booking.insufficientTimeModal.phone2')}`;
        const phone2Line = document.createElement('p');
        phone2Line.className = 'booking__modal-contact-line';
        phone2Line.appendChild(phone2Link);
        const emailLink = document.createElement('a');
        emailLink.href = `mailto:ladyspace.cyprus@gmail.com`;
        emailLink.textContent = t('booking.insufficientTimeModal.email');
        const emailLine = document.createElement('p');
        emailLine.className = 'booking__modal-contact-line';
        emailLine.appendChild(emailLink);
        contacts.append(phone1Line, phone2Line, emailLine);
        const actions = document.createElement('div');
        actions.className = 'booking__modal-actions';
        const okButton = document.createElement('button');
        okButton.type = 'button';
        okButton.className = 'cta';
        okButton.textContent = t('booking.leadTimeModal.okButton');
        okButton.addEventListener('click', () => overlay.remove());
        actions.appendChild(okButton);
        modal.append(body, contactsLabel, contacts, actions);
        overlay.appendChild(modal);
        document.body.appendChild(overlay);
    }
    prevButton.addEventListener('click', () => {
        viewMonth -= 1;
        if (viewMonth < 0) {
            viewMonth = 11;
            viewYear -= 1;
        }
        renderCalendarGrid();
    });
    nextButton.addEventListener('click', () => {
        viewMonth += 1;
        if (viewMonth > 11) {
            viewMonth = 0;
            viewYear += 1;
        }
        renderCalendarGrid();
    });
    submitButton.addEventListener('click', () => {
        if (submitButton.disabled || selectionState.start === null || selectionState.end === null)
            return;
        if (!hasMinLeadTime(selectionState.start)) {
            openLeadTimeBlockedModal();
            renderSummaryAndBar();
            return;
        }
        const duration = selectionState.end - selectionState.start;
        const insufficientSubscription = !forceHourlyMode &&
            subscriptionLeftMinutes !== undefined &&
            subscriptionLeftMinutes !== null &&
            subscriptionLeftMinutes > 0 &&
            subscriptionLeftMinutes < duration;
        if (insufficientSubscription && place.pricePerHour != null) {
            openInsufficientTimeModal();
            return;
        }
        submitButton.disabled = true;
        submitButton.textContent = t('booking.submitting');
        if (!pendingIdempotencyKey)
            pendingIdempotencyKey = createIdempotencyKey();
        createBooking({
            placeId: place.id,
            date: dateKey(selectedDate),
            startMinutes: selectionState.start,
            endMinutes: selectionState.end,
            locale: i18nService.getLocale(),
            forceHourly: forceHourlyMode,
            idempotencyKey: pendingIdempotencyKey,
        })
            .then((result) => {
            pendingIdempotencyKey = null;
            bookingConfirmed = true;
            submitButton.textContent = t('booking.submitted');
            if (result.booking.subscriptionLeftMinutes !== null) {
                subscriptionLeftMinutes = result.booking.subscriptionLeftMinutes;
                renderBalanceLine();
            }
            if (result.booking.paymentType === 'hourly' && result.booking.priceCents != null) {
                const note = document.createElement('p');
                note.className = 'booking__summary booking__pay-note';
                note.textContent = t('booking.payOnSiteNotice', { price: formatCentsAsEuros(result.booking.priceCents) });
                availability.appendChild(note);
            }
        })
            .catch((error) => {
            const code = error instanceof ApiError ? error.code : undefined;
            if (code === 'too_early') {
                openLeadTimeBlockedModal();
                submitButton.disabled = true;
                submitButton.textContent = t('booking.submit');
                return;
            }
            const messageKey = code === 'no_active_subscription'
                ? 'booking.errors.noSubscription'
                : code === 'slot_unavailable'
                    ? 'booking.errors.slotUnavailable'
                    : code === 'day_closed'
                        ? 'booking.errors.dayClosed'
                        : 'booking.errors.generic';
            const existingError = availability.querySelector('.booking__error');
            if (existingError)
                existingError.remove();
            const errorMessage = document.createElement('p');
            errorMessage.className = 'auth__error booking__error';
            errorMessage.textContent = t(messageKey);
            availability.appendChild(errorMessage);
            submitButton.disabled = false;
            submitButton.textContent = t('booking.submit');
        });
    });
    renderCalendarGrid();
    renderBalanceLine();
    loadAvailability();
    fetchSubscriptionBalance(place.id)
        .then((res) => {
        subscriptionLeftMinutes = res.leftMinutes;
        renderBalanceLine();
        renderSummaryAndBar();
    })
        .catch((error) => {
        console.error('Failed to load subscription balance:', error);
        subscriptionLeftMinutes = null;
        renderBalanceLine();
    });
    return root;
}
